// The desktop: the one place the kernel, the host layer and the active skin are composed.
//
// Layer order is fixed by `shell.css`: surface, windows, bar, overlays, shell UI. Only the
// window layer is stable-by-construction (§5.7); everything above it is free to re-render.
import { useCallback, useEffect, useRef, useState, type DragEvent as ReactDragEvent } from 'react';
import { USER_FOLDERS, joinVfs, userRoot, type Win } from '@hangar/shared';
import { workAreaFor } from '@/kernel/wm/geometry';
import { importTransfer } from '@/kernel/vfs/import';
import { COPY } from '@/copy';
import { useKernel, useSetting } from '@/kernel/react';
import { mountAppFrame, type AppFrame } from '@/kernel/bridge/frames';
import { AppHost, type HostSlots } from './AppHost';
import { AppErrorLayer, type FailedApp } from './AppErrorLayer';
import { AppSkeletonLayer } from './AppSkeletonLayer';
import { SkinProvider, useSkin } from './SkinProvider';
import { WindowFrameLayer } from './WindowFrameLayer';
import { ShellUiHost } from './ShellUiHost';
import { InviteLayer } from './InviteLayer';
import { GuestBanner } from './GuestBanner';
import { ClipboardPanel } from '@/skins/clipboard';
import { NotificationCenter } from '@/skins/notifications';
import { SyncChip } from './SyncChip';
import { DragLayer } from './DragLayer';

function DesktopBody({ onExport, onSignIn }: DesktopProps) {
  const kernel = useKernel();
  const skin = useSkin();
  const [slots, setSlots] = useState<ReadonlyMap<string, HostSlots>>(() => new Map());
  /** §9.4 — the live app frames, one per external window. Torn down only on close (§5.7). */
  const frames = useRef(new Map<string, AppFrame>());
  /** §9.6 — external windows whose origin did not answer, showing the shell-drawn error instead. */
  const [failed, setFailed] = useState<ReadonlyMap<string, FailedApp>>(() => new Map());
  /** §18 F10 — windows whose frame was unloaded, showing the skeleton until it is back. */
  const [suspended, setSuspended] = useState<ReadonlyMap<string, string>>(() => new Map());
  /** Which hosts are attached right now, readable from an async callback without going stale. */
  const attached = useRef(new Map<string, HostSlots>());
  /** External windows, whether they hold a frame, the error panel, or a probe still in flight. */
  const externalKeys = useRef(new Set<string>());
  /** Hosts whose teardown is scheduled but not yet run. A re-attach cancels it (D-125). */
  const closing = useRef(new Set<string>());
  /**
   * External windows whose §9.6 launch probe is still in flight. The probe is asynchronous, so
   * the slot has to be claimed *synchronously* — otherwise a re-attach arriving before it
   * resolves starts a second mount and the window ends up with two iframes (D-125).
   */
  const probing = useRef(new Set<string>());
  /** Frames that have loaded at least once, so a *second* load is a navigation (§10). */
  const loaded = useRef(new Set<string>());

  const { DesktopSurface, Bar, Launcher, Switcher, NotificationHost, LockScreen } = skin.components;

  // The work area is the viewport minus the skin's bar. Recomputed on resize and skin change.
  useEffect(() => {
    const measure = (): void => {
      const style = getComputedStyle(document.documentElement);
      const px = (name: string): number => {
        const value = Number.parseInt(style.getPropertyValue(name) || '0', 10);
        return Number.isFinite(value) ? value : 0;
      };
      // `--bar-height` is §6.2's token and every skin has one at the bottom. A skin with chrome at
      // the **top** as well — the macOS menu bar — says so with `--work-area-top`, which is not a
      // §6.2 token (that set is closed) but a plain variable its own sheet declares (D-144).
      // A skin that declares neither gets 0, which is what "no chrome up there" means.
      kernel.setWorkArea(
        workAreaFor(
          { x: 0, y: 0, w: window.innerWidth, h: window.innerHeight },
          { top: px('--work-area-top'), right: 0, bottom: px('--bar-height'), left: 0 },
        ),
      );
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [kernel, skin.id]);

  // §5.4 — the active skin owns window semantics; the kernel holds whichever is mounted.
  useEffect(() => {
    kernel.setPolicy(skin.policy);
  }, [kernel, skin.policy]);

  // §5.2 — the snap overlay is one element the engine mutates in place. It is handed over once;
  // nothing about it ever passes through React state.
  const snapOverlay = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    kernel.snap.attach(snapOverlay.current);
    return () => kernel.snap.attach(null);
  }, [kernel]);

  // §6.4 — the shell's global key handler, in the capture phase, with the active skin's keymap
  // and the user's per-binding overrides (§11.6).
  useEffect(() => {
    kernel.keyboard.setKeymap(skin.keymap);
    return kernel.keyboard.attach(document);
  }, [kernel, skin.keymap]);

  // §11.6 / §34.4 (D-544) — through the kernel rather than straight at the keyboard service: two
  // settings feed one override table, and `setOverrides` replaces it whole.
  const overrides = useSetting('keymap.overrides');
  const captureChord = useSetting('notes.captureChord');
  useEffect(() => {
    void overrides;
    void captureChord;
    kernel.applyKeyOverrides();
  }, [kernel, overrides, captureChord]);

  // §18 F4 — a write that ran out of room. The app already has `QUOTA_EXCEEDED`; this is the
  // half the *user* sees, and it offers the two things that actually make room rather than
  // describing the problem and stopping.
  useEffect(
    () =>
      kernel.bus.on('fs:quotaExceeded', () => {
        void kernel.shellui
          .confirm(COPY.quotaFull, undefined, {
            confirmLabel: COPY.quotaExport,
            cancelLabel: COPY.quotaEmptyTrash,
          })
          .then((exportInstead) => {
            // Export is the shell's own — the same one the guest banner offers.
            if (exportInstead) onExport();
            else void kernel.trash.empty();
          });
      }),
    [kernel, onExport],
  );

  // Escape closes whatever overlay is open, wherever focus happens to be.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      if (kernel.shellui.activeOverlay() === null) return;
      event.preventDefault();
      kernel.shellui.closeOverlay();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [kernel]);

  // Any pointer landing inside a window focuses it. Capture phase, so an app cannot swallow it.
  const onPointerDownCapture = useCallback(
    (event: React.PointerEvent) => {
      const host = (event.target as HTMLElement).closest<HTMLElement>('.win-host');
      const id = host?.dataset['windowId'];
      if (id && kernel.wm.getState().focusedId !== id) kernel.wm.focus(id);
      kernel.sound.noteUserGesture();
    },
    [kernel],
  );

  // §7.7 — a drop from the host OS onto the desktop lands in the user's Desktop folder. The
  // walk is the kernel's (one implementation, shared with Explorer's drop); the writes go
  // through the ordinary tree, so `/System` and a bad name are refused exactly as always.
  const onDesktopDrop = useCallback(
    (event: ReactDragEvent) => {
      if (!event.dataTransfer.types.includes('Files')) return;
      if ((event.target as HTMLElement).closest('.win-host')) return;
      event.preventDefault();
      const into = joinVfs(userRoot(kernel.vfs.currentUser()), USER_FOLDERS[0]);
      void importTransfer(event.dataTransfer, into, {
        write: (path, bytes) => kernel.vfs.write(path, bytes),
        mkdir: (path) => kernel.vfs.mkdir(path),
        taken: async (dir) => (await kernel.vfs.list(dir)).map((entry) => entry.name),
      }).catch((error: unknown) => console.warn('desktop import failed', error));
    },
    [kernel],
  );

  // §9.4 — put the app's iframe in the window. Separate from the attach path because §9.6's Retry
  // is the second caller: a window that opened onto the error panel gets its frame later, into the
  // very same slot, which is exactly what §5.7 permits and re-parenting would not be.
  const mountExternal = useCallback(
    (win: Win, content: HTMLElement) => {
      const external = kernel.installed.get(win.appId);
      if (!external) return;
      frames.current.set(
        win.instanceKey,
        mountAppFrame({
          bridge: kernel.bridge,
          manifest: external.manifest,
          instanceKey: win.instanceKey,
          container: content,
          shellOrigin: window.location.origin,
          welcome: () => kernel.welcomeFor(win.appId),
          onLoad: () => {
            // §10 — "expires on the receiving window's next navigation". A frame `load` after the
            // first one *is* that navigation: the document that was going to redeem the token no
            // longer exists, so the token must not either.
            if (loaded.current.has(win.instanceKey)) kernel.transfer.navigated(win.instanceKey);
            loaded.current.add(win.instanceKey);

            // F10 — the skeleton stays up until the rebuilt frame has actually loaded, so the
            // window never shows a blank one. A first mount has no skeleton and this is a no-op.
            setSuspended((prev) => {
              if (!prev.has(win.instanceKey)) return prev;
              const next = new Map(prev);
              next.delete(win.instanceKey);
              return next;
            });
          },
        }),
      );
    },
    [kernel],
  );

  // §9.6 / F10 — the kernel decides *when*; the iframe is the host layer's, so this is where an
  // unload and a rebuild actually happen (§6.5).
  useEffect(() => {
    const offUnloaded = kernel.bus.on('app:unloaded', ({ instanceKey, appId }) => {
      const frame = frames.current.get(instanceKey);
      if (!frame) return;
      frames.current.delete(instanceKey);
      // The frame goes; the `.win-host` does not (§5.7). The window is still open, still in the
      // bar, and its session blob is still filed under this same instance key.
      frame.destroy();
      setSuspended((prev) => new Map(prev).set(instanceKey, appId));
    });

    const offResumed = kernel.bus.on('app:resumed', ({ instanceKey }) => {
      const slot = attached.current.get(instanceKey);
      const win = kernel.wm.list().find((candidate) => candidate.instanceKey === instanceKey);
      if (!slot || !win || frames.current.has(instanceKey)) return;
      // Into the *same* slot the unloaded frame came out of — which is what §5.7 permits and a
      // re-parent would not be. The app's blob is what puts the user back (§5.6).
      mountExternal(win, slot.content);
    });

    return () => {
      offUnloaded();
      offResumed();
    };
  }, [kernel, mountExternal]);

  // §9.6 — the launch-time health check. It runs *before* an iframe exists, because PLAN §5.4 says
  // an unreachable app must produce the shell's error window "rather than a blank iframe" (D-117).
  const attachExternal = useCallback(
    async (win: Win, content: HTMLElement): Promise<void> => {
      const external = kernel.installed.get(win.appId);
      if (!external) return;
      const reachable =
        // Already dimmed: §9.6 says launching an unhealthy app opens the error window, and the
        // three misses that dimmed it are the answer. No fourth probe to make the user wait for.
        external.health === 'unhealthy' ? false : await kernel.health.check(win.appId);
      // The window may have closed while the probe was in flight.
      if (!attached.current.has(win.instanceKey)) return;
      if (reachable) {
        mountExternal(win, content);
        return;
      }
      setFailed((prev) =>
        new Map(prev).set(win.instanceKey, {
          winId: win.id,
          appId: win.appId,
          name: external.manifest.name,
          entry: external.manifest.entry,
        }),
      );
    },
    [kernel, mountExternal],
  );

  const retryFailed = useCallback(
    (instanceKey: string) => {
      const app = failed.get(instanceKey);
      const slot = attached.current.get(instanceKey);
      const win = app ? kernel.wm.get(app.winId) : undefined;
      if (!app || !slot || !win) return;
      void kernel.health.check(app.appId).then((healthy) => {
        if (!healthy || !attached.current.has(instanceKey)) return;
        setFailed((prev) => {
          const next = new Map(prev);
          next.delete(instanceKey);
          return next;
        });
        mountExternal(win, slot.content);
      });
    },
    [failed, kernel, mountExternal],
  );

  // §9.6 — the *user* uninstalling is the only uninstall there is; nothing here is automatic.
  const uninstallFailed = useCallback(
    (instanceKey: string) => {
      const app = failed.get(instanceKey);
      if (!app) return;
      void kernel.installed.uninstall(app.appId).then(() => kernel.wm.close(app.winId));
    },
    [failed, kernel],
  );

  const onDesktopDragOver = useCallback((event: ReactDragEvent) => {
    if (!event.dataTransfer.types.includes('Files')) return;
    if ((event.target as HTMLElement).closest('.win-host')) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  return (
    <div
      className="hangar-desktop"
      data-testid="hangar-desktop"
      onPointerDownCapture={onPointerDownCapture}
      onDragOver={onDesktopDragOver}
      onDrop={onDesktopDrop}
    >
      <div className="desktop-surface-layer">
        <DesktopSurface />
      </div>

      <AppHost
        wm={kernel.wm}
        onHostAttached={(win, hostSlots) => {
          attached.current.set(win.instanceKey, hostSlots);
          // A re-attach of a host whose teardown is still pending is not a new window (D-125) —
          // §5.7's whole point is that re-creating this frame would reload the app.
          closing.current.delete(win.instanceKey);

          // §9.1 vs §9.4 — a built-in is mounted into the slot; an installed app gets an iframe
          // in the same slot, once §9.6's launch check says its origin is there. Everything above
          // this line is identical for both.
          if (kernel.installed.has(win.appId)) {
            externalKeys.current.add(win.instanceKey);
            if (frames.current.has(win.instanceKey) || probing.current.has(win.instanceKey)) return;
            probing.current.add(win.instanceKey);
            void attachExternal(win, hostSlots.content).finally(() =>
              probing.current.delete(win.instanceKey),
            );
            return;
          }
          kernel.apps.mount(win, hostSlots.content);
        }}
        onHostDetached={(instanceKey) => {
          attached.current.delete(instanceKey);
          if (!externalKeys.current.has(instanceKey)) {
            // The mount service defers and cancels its own teardown for the same reason (D-125).
            kernel.apps.unmount(instanceKey);
            return;
          }

          // Deferred so a StrictMode detach-and-re-attach, which is not a close at all, cannot
          // destroy a live iframe and reload the app inside it (§5.7, D-125).
          closing.current.add(instanceKey);
          queueMicrotask(() => {
            if (!closing.current.delete(instanceKey)) return;
            externalKeys.current.delete(instanceKey);
            probing.current.delete(instanceKey);
            loaded.current.delete(instanceKey);
            setFailed((prev) => {
              if (!prev.has(instanceKey)) return prev;
              const next = new Map(prev);
              next.delete(instanceKey);
              return next;
            });
            setSuspended((prev) => {
              if (!prev.has(instanceKey)) return prev;
              const next = new Map(prev);
              next.delete(instanceKey);
              return next;
            });
            // No frame when the origin was down, or when the probe was still in flight — the
            // window closing is what §9.6 leaves behind in both cases.
            const frame = frames.current.get(instanceKey);
            frames.current.delete(instanceKey);
            frame?.destroy();
          });
        }}
        onSlotsChanged={setSlots}
      />
      <WindowFrameLayer slots={slots} />
      <AppErrorLayer
        failed={failed}
        slots={slots}
        onRetry={retryFailed}
        onUninstall={uninstallFailed}
      />
      <AppSkeletonLayer suspended={suspended} slots={slots} />

      <div className="snap-overlay-layer" aria-hidden>
        <div className="snap-overlay" data-testid="snap-overlay" data-visible="false" ref={snapOverlay} />
      </div>

      <div className="bar-layer">
        <Bar />
      </div>

      {/* §10.1 — one panel, placed by whichever skin is mounted (D-135). */}
      <ClipboardPanel />

      {/* §13 — the Center, one component for the same reason (D-140). The *toast* is the skin's. */}
      <NotificationCenter />
      {/* §18 F1 — the tray's offline state, placed by each skin's sheet. */}
      <SyncChip />

      {/* §10.2 — the shell's own drag overlay. Renders nothing until a drag is in flight. */}
      <DragLayer />

      <div className="overlay-layer">
        <Launcher />
        <Switcher />
        <NotificationHost />
        <LockScreen />
      </div>

      <GuestBanner onExport={onExport} onSignIn={onSignIn} />

      {/* §26.4 — above every frame, and deliberately outside `.shellui-layer`: that one is the
          modal surface, and an invitation must not stop anybody working.

          `onJoin` is supplied here rather than resolved inside the layer, because joining is a
          §26.7 act — accept the session, then open the game on it — and the card's job ends at
          "the user said yes". */}
      <InviteLayer
        onJoin={(sessionId, gameId) => {
          void kernel.joinPlay(sessionId, gameId).catch((error: unknown) => {
            // A card for a game that has already been withdrawn is the case this covers, and the
            // honest thing to say is that it is over — not to open an empty window on it.
            kernel.shellui.showToast(COPY.game.gameEnded);
            console.warn('join', error);
          });
        }}
        // §34.13 — the same arrangement for a file offer, and for the same reason: accepting is a
        // server round trip that lands a file and may be refused for space (§34.17), so the card's
        // job ends at "the user said yes" and the kernel decides whether the card goes away.
        onAnswerOffer={(id, answer) => {
          void kernel.answerFileOffer(id, answer);
        }}
      />

      <div className="shellui-layer">
        <ShellUiHost />
      </div>
    </div>
  );
}

export interface DesktopProps {
  /** §7.7 — "Export everything", the guest banner's first action. */
  onExport: () => void;
  /** §8.1 — replaced by the real OIDC start at M12.4. */
  onSignIn: () => void;
}

export function Desktop(props: DesktopProps) {
  return (
    <SkinProvider>
      <DesktopBody {...props} />
    </SkinProvider>
  );
}
